import { SorterResult } from 'antd/lib/table/interface';
import { makeAutoObservable, runInAction } from 'mobx';
import { tasksApi } from '@tools/ui/api';
import { BOTTOM_LIMIT_RANGE, TOP_LIMIT_RANGE } from '@tools/ui/consts';
import { ViewName } from '@tools/ui/enums';
import { ITableResponse, ParsedQuery } from '@tools/ui/types';
import {
  GenericTask,
  SortFindManyTaskInput,
  Task
} from '@tools/ui/types/generated/graphql';
import { isJsonString } from '@tools/ui-utils/json.util';
import { taskQuerySortBuilder } from '@tools/ui-utils/query-sort.util';

export type IInboxFilter = { AND: { OR: [JSON] }[] };

class TaskStore {
  allTasks?: ITableResponse<Task>;

  taskAmount = 0;

  refetchTaskAmount: VoidFunction;

  querySort: SortFindManyTaskInput[] = [];

  pagination = {
    pageSize: BOTTOM_LIMIT_RANGE,
    currentPage: 1
  };

  selectedTask: Task | null = null;

  refetchGenericTasks: VoidFunction;

  refetchTask: VoidFunction;

  selectedGenericTask: GenericTask | null | undefined = null;

  filterChanged = false;

  qbSharedData: { webAddressUri?: string } = {};

  dateFilter?: Record<string, boolean> = {};

  hasTaskChanged = false;

  constructor() {
    makeAutoObservable(this);
  }

  setHasTaskChanged = () => {
    this.hasTaskChanged = !this.hasTaskChanged;
  };

  setRefetchGenericTasks = (refetchGenericTasks: VoidFunction) => {
    this.refetchGenericTasks = refetchGenericTasks;
  };

  setRefetchTask = (refetchTask: VoidFunction) => {
    this.refetchTask = refetchTask;
  };

  setWebAddressUri = (uri: string) => {
    runInAction(() => {
      this.qbSharedData.webAddressUri = uri;
    });
  };

  setTasks = (tasks: Task[]): void => {
    const withoutDeleted = tasks.filter((task) => task.entityCachedData);
    if (this.filterChanged) {
      this.allTasks = { data: withoutDeleted };
    } else if (withoutDeleted.length === 0) {
      this.allTasks = { data: [] };
    } else {
      this.allTasks = { data: withoutDeleted };
    }
  };

  setTaskAmount = (taskCount?: number) => {
    this.taskAmount = taskCount || 0;
  };

  setRefetchTaskAmount = (refetchTaskAmount: VoidFunction) => {
    this.refetchTaskAmount = refetchTaskAmount;
  };

  updateTask = async (task: Task): Promise<void> => {
    await tasksApi.updateTask(task);
  };

  sortWithoutDefaultSort = (
    sort: SortFindManyTaskInput[],
    defaultSort: SortFindManyTaskInput[]
  ) => {
    return sort.filter((curSortOption) => !defaultSort.includes(curSortOption));
  };

  setSort = (
    sorterResult: SorterResult<Task>[],
    isDefaultSort: boolean,
    defaultSort: SortFindManyTaskInput[]
  ) => {
    let sort: SortFindManyTaskInput[] = sorterResult.reduce(
      taskQuerySortBuilder,
      sorterResult.length ? [] : this.querySort
    );

    if (!isDefaultSort && defaultSort)
      sort = this.sortWithoutDefaultSort(sort, defaultSort);

    this.querySort = [...new Set(sort)];
  };

  getSort = () => this.querySort;

  setSelectedTask = (task: Task | null) => {
    this.selectedTask = task;
  };

  setSelectedGenericTask = (
    selectedGenericTask: GenericTask | null | undefined
  ) => {
    if (selectedGenericTask) this.selectedGenericTask = selectedGenericTask;
  };

  resetSelectedTask = () => {
    this.selectedGenericTask = null;
  };

  parseQuery = ({
    sort,
    filters,
    limit,
    page,
    paymentId,
    defaultTab,
    viewName
  }: {
    filters: string;
    sort: string;
    page: string;
    limit: string;
    paymentId?: string;
    defaultTab?: string;
    viewName?: ViewName;
  }): ParsedQuery => {
    try {
      return {
        ...(sort?.length ? { sort: JSON.parse(sort) } : { sort: [] }),
        ...(filters && isJsonString(filters)
          ? { filters: JSON.parse(filters) }
          : {}),
        paymentId: paymentId && +paymentId,
        defaultTab,
        viewName,
        limit: Math.min(
          Math.max(Number(limit), BOTTOM_LIMIT_RANGE),
          TOP_LIMIT_RANGE
        ),
        page: Number(page)
      } as ParsedQuery;
    } catch {
      return {} as ParsedQuery;
    }
  };

  unreadTask = async (task: Task): Promise<void> => {
    await tasksApi.unreadTask(task._id);
  };

  updateTasksLabel = async (): Promise<void> => {
    await tasksApi.updateTaskLabel();
  };
}

export default new TaskStore();
